import { useMemo } from 'react';

import { useQueryClient } from '@tanstack/react-query';
import {
  getApiV5FirmsDocumentsDocumentUuidRotation,
  getGetApiV5FirmsDocumentsDocumentUuidRotationQueryKey,
  putApiV5FirmsDocumentsDocumentUuidRotation,
} from '@liscio/api';
import type { LiscioPSPDFKitInstance } from 'types/global';
import type {
  RotationDegrees,
  RotationPayload,
  RotationSource,
} from 'common/PSPDFKit/types';

export type DocumentRotationSource = RotationSource & {
  /** Reads the saved rotation for the shared viewer, which needs no SDK instance. */
  load: () => Promise<RotationPayload>;
};

// Normalize an arbitrary degree value to its mod-360 equivalent in [0, 360).
// Returns NaN if `v` isn't a finite number (so the downstream membership
// check fails closed).
const normalizeDegrees = (v: number): number =>
  Number.isFinite(v) ? (((v % 360) + 360) % 360) : Number.NaN;

// True iff `v` is exactly one of the canonical RotationDegrees values
// (0/90/180/270). Explicit membership rather than a mod-90 trick — that
// trick incorrectly accepted values like 360, 450 etc. as RotationDegrees,
// making the type guard unsound (TS would treat 360 as `0 | 90 | 180 | 270`).
const isQuarterTurn = (v: unknown): v is RotationDegrees =>
  v === 0 || v === 90 || v === 180 || v === 270;

const isPagesMap = (
  value: unknown,
): value is RotationPayload['pages'] => {
  if (!value || typeof value !== 'object' || Array.isArray(value)) return false;
  return Object.values(value as Record<string, unknown>).every(isQuarterTurn);
};

const extractPagesMap = (value: unknown): RotationPayload['pages'] => {
  if (!value || typeof value !== 'object') return {};
  const { pages } = value as { pages?: unknown };
  return isPagesMap(pages) ? pages : {};
};

type RotateOperation = {
  type: 'rotatePages';
  pageIndexes: number[];
  rotateBy: 0 | 90 | 180 | 270;
};

const applyRotationsToInstance = (
  instance: LiscioPSPDFKitInstance,
  pages: RotationPayload['pages'],
): void => {
  const ops: RotateOperation[] = [];
  Object.entries(pages).forEach(([key, target]) => {
    const idx = Number(key);
    if (!Number.isInteger(idx) || idx < 0) return;
    const current = instance.pageInfoForIndex(idx)?.rotation ?? 0;
    const delta = normalizeDegrees(target - current);
    if (delta === 0) return;
    // PSPDFKit `rotateBy` only accepts the four canonical quarter-turns. The
    // type narrowing on `target` and the runtime guard via `isPagesMap` mean
    // this is already true in practice, but PageInfo.rotation is typed as
    // generic `number` upstream — guarding here protects against a future
    // PSPDFKit regression that returns a non-quarter rotation.
    if (!isQuarterTurn(delta)) return;
    ops.push({
      type: 'rotatePages',
      pageIndexes: [idx],
      rotateBy: delta,
    });
  });
  if (ops.length > 0) {
    instance.applyOperations(ops);
  }
};

/**
 * Returns a stable rotation source for either PDF viewer, or undefined when
 * the document UUID isn't known yet. Identity is memoized on documentUuid so
 * parent re-renders don't re-wire viewer effects.
 *
 * `load` is for the shared viewer and rejects like its other reads; `hydrate` is
 * for PSPDFViewer and never throws. All three use the absolute per-page rotation
 * (degrees clockwise, 0/90/180/270). On hydrate, the diff between the
 * persisted target and the live instance's `pageInfoForIndex(N).rotation`
 * is applied as a single batched `applyOperations` call, so re-hydrating
 * an already-rotated state is a no-op.
 */
export function useDocumentRotationSource(
  documentUuid: string | null | undefined,
): DocumentRotationSource | undefined {
  const queryClient = useQueryClient();

  return useMemo(() => {
    if (!documentUuid) return undefined;

    const key =
      getGetApiV5FirmsDocumentsDocumentUuidRotationQueryKey(documentUuid);

    return {
      load: async () => {
        const response = await queryClient.fetchQuery({
          queryKey: key,
          queryFn: () =>
            getApiV5FirmsDocumentsDocumentUuidRotation(documentUuid),
        });
        return { pages: extractPagesMap(response?.data?.rotation) };
      },
      hydrate: async (instance) => {
        try {
          const response = await queryClient.fetchQuery({
            queryKey: key,
            queryFn: () =>
              getApiV5FirmsDocumentsDocumentUuidRotation(documentUuid),
          });
          const pages = extractPagesMap(response?.data?.rotation);
          applyRotationsToInstance(instance, pages);
        } catch (err) {
          // Persistence is auxiliary — render at default rotation rather than nothing.
          // eslint-disable-next-line no-console
          console.error('[PSPDFViewer] failed to hydrate rotation', err);
        }
      },
      save: async (payload) => {
        const safe: RotationPayload = isPagesMap(payload?.pages)
          ? payload
          : { pages: {} };
        const response = await putApiV5FirmsDocumentsDocumentUuidRotation(
          documentUuid,
          { rotation: safe },
        );
        queryClient.setQueryData(key, response);
      },
    };
  }, [documentUuid, queryClient]);
}
